// src/App.js - WITH AUDIT DASHBOARD TAB ADDED

import React, { useState, useEffect } from 'react';
import { ChefHat, Users, Package, Settings, LogOut, ShoppingCart, TrendingUp, UtensilsCrossed, Tag, QrCode, Loader, Menu, X, Bug, FileText, Shield, DollarSign } from 'lucide-react';
import { AppProvider, useApp } from './context/AppContext';
import StorageService from './services/StorageService';
import SubscriptionService from './services/SubscriptionService';
import KitchenStaffService from './services/KitchenStaffService';
import NotificationBell from './components/NotificationBell';
import BugReportButton from './components/BugReportButton';
import migratePasswords from './utils/migratePasswords';
//import { ..., DollarSign } from 'lucide-react';

// Pages
import Login from './pages/Auth/Login';
import SuperAdminDashboard from './pages/SuperAdmin/Dashboard';
import SuperAdminRestaurants from './pages/SuperAdmin/Restaurants';
import SuperAdminPackages from './pages/SuperAdmin/Packages';
import SuperAdminSettings from './pages/SuperAdmin/Settings';
import SuperAdminBugReports from './pages/SuperAdmin/BugReports';
import OwnerDashboard from './pages/RestaurantOwner/Dashboard';
import OwnerMenu from './pages/RestaurantOwner/Menu';
import OwnerOrders from './pages/RestaurantOwner/Orders';
import OwnerSettings from './pages/RestaurantOwner/Settings';
import Categories from './pages/RestaurantOwner/Categories';
import Tables from './pages/RestaurantOwner/Tables';
import InvoiceManagementPanel from './pages/RestaurantOwner/InvoiceManagementPanel';
import AuditDashboard from './pages/RestaurantOwner/AuditDashboard'; // ✅ NEW IMPORT
import CustomerHome from './pages/Customer/Home';
import OrderTracking from './pages/Customer/OrderTracking';
import Inventory from './pages/RestaurantOwner/Inventory';
import ProfitLoss from './pages/RestaurantOwner/ProfitLoss';

const AppContent = () => {
  const [currentView, setCurrentView] = useState('login');
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [restaurantId, setRestaurantId] = useState(null);
  const [kitchenStaffId, setKitchenStaffId] = useState(null);
  const [tableId, setTableId] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, setUserType } = useApp();

  useEffect(() => {
    const loadKitchenRestaurant = async () => {
      if (restaurantId && currentView === 'kitchen') {
        setLoading(true);
        try {
          const rest = await StorageService.getRestaurant(restaurantId);
          setRestaurant(rest);
        } catch (error) {
          console.error('Error loading restaurant for kitchen:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    loadKitchenRestaurant();
  }, [restaurantId, currentView]);
  // Password migration
  useEffect(() => {
    const runPasswordMigration = async () => {
      const migrationDone = localStorage.getItem('passwordMigrationCompleted');
      
      if (!migrationDone) {
        console.log('🔄 Starting password migration...');
        
        try {
          const result = await migratePasswords();
          
          if (result.success) {
            localStorage.setItem('passwordMigrationCompleted', 'true');
            localStorage.setItem('passwordMigrationDate', new Date().toISOString());
            
            console.log('✅ Password migration completed successfully!');
            console.log(`📊 Migrated: ${result.migrated}, Skipped: ${result.skipped}`);
            
            if (result.migrated > 0) {
              alert(
                `✅ Password Migration Completed!\n\n` +
                `Migrated: ${result.migrated} password(s)\n` +
                `Already hashed: ${result.skipped} password(s)\n\n` +
                `Your passwords are now securely encrypted!`
              );
            }
          } else {
            console.error('❌ Password migration failed:', result.error);
          }
        } catch (error) {
          console.error('❌ Migration error:', error);
        }
      } else {
        console.log('✅ Password migration already completed');
        const migrationDate = localStorage.getItem('passwordMigrationDate');
        if (migrationDate) {
          console.log('📅 Migration date:', new Date(migrationDate).toLocaleString());
        }
      }
    };

    const timer = setTimeout(() => {
      runPasswordMigration();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Subscription checking - runs every hour
  useEffect(() => {
    const checkSubscriptions = async () => {
      try {
        console.log('🔍 Running automatic subscription check...');
        const result = await SubscriptionService.checkAndDeactivateExpiredSubscriptions();
        
        if (result.deactivatedCount > 0) {
          console.log(`⚠️ ${result.deactivatedCount} restaurant(s) auto-deactivated due to expired subscription`);
        }
        
        if (result.expiringSoonCount > 0) {
          console.log(`⏰ ${result.expiringSoonCount} restaurant(s) expiring within 5 days`);
        }
      } catch (error) {
        console.error('❌ Error in automatic subscription check:', error);
      }
    };

    const initialTimer = setTimeout(checkSubscriptions, 5000);
    const interval = setInterval(checkSubscriptions, 60 * 60 * 1000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);

const handleLogin = async (type, restId) => {
  console.log('🎯 handleLogin called:', { type, restId });
  
  if (type === 'kitchen') {
    setCurrentView('kitchen');
    setRestaurantId(restId);
    const staffId = localStorage.getItem('kitchenStaffId');
    setKitchenStaffId(staffId);
   setUserType('kitchen');

  } else {
    setCurrentView(type);
    if (restId) {
      setRestaurantId(restId);
    }
  }
  
  setActiveTab('dashboard');
};

  const handleTrackOrder = (sessionId) => {
    setCurrentSessionId(sessionId);
    setCurrentView('orderTracking');
  };

  const handleLogout = () => {
    logout();
    setCurrentView('login');
    setActiveTab('dashboard');
    setRestaurantId(null);
    setTableId(null);
    setRestaurant(null);
    setMobileMenuOpen(false);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const path = window.location.pathname;
    const matchWithTable = path.match(/\/customer\/([^/]+)\/([^/]+)/);
    const matchWithoutTable = path.match(/\/customer\/([^/]+)$/);

    if (matchWithTable) {
      setRestaurantId(matchWithTable[1]);
      setTableId(matchWithTable[2]);
      setCurrentView('customer');
    } else if (matchWithoutTable) {
      setRestaurantId(matchWithoutTable[1]);
      setTableId(null);
      setCurrentView('customer');
    }
  }, []);

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (restaurantId && currentView === 'owner') {
        setLoading(true);
        try {
          const data = await StorageService.getData();
          const rest = data.restaurants.find(r => r.id === restaurantId);
          setRestaurant(rest);
        } catch (error) {
          console.error('Error fetching restaurant:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchRestaurant();
  }, [restaurantId, currentView]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  if (currentView === 'login') {
    return <Login onLogin={handleLogin} />;
  }

  if (currentView === 'customer') {
    return (
      <CustomerHome
        restaurantId={restaurantId}
        tableId={tableId}
        onTrackOrder={handleTrackOrder}
      />
    );
  }

  if (currentView === 'orderTracking') {
    return (
      <OrderTracking
        sessionId={currentSessionId}
        onBackToMenu={() => setCurrentView('customer')}
      />
    );
  }

  // Super Admin Layout
  if (currentView === 'superadmin') {
    return (
      <div className="flex flex-col lg:flex-row h-screen bg-gray-100">
        <div className="hidden lg:block lg:w-64 bg-white shadow-lg overflow-y-auto">
          <div className="p-6 bg-gradient-to-r from-orange-500 to-red-600">
            <div className="flex items-center gap-3 text-white">
              <ChefHat className="w-8 h-8" />
              <div>
                <h2 className="font-bold text-lg">Super Admin</h2>
                <p className="text-sm text-orange-100">Management Panel</p>
              </div>
            </div>
          </div>
          <nav className="p-4">
            <button onClick={() => handleTabClick('dashboard')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'dashboard' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <TrendingUp className="w-5 h-5" />
              Dashboard
            </button>
            <button onClick={() => handleTabClick('restaurants')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'restaurants' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <Users className="w-5 h-5" />
              Restaurants
            </button>
            <button onClick={() => handleTabClick('packages')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'packages' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <Package className="w-5 h-5" />
              Packages
            </button>
            <button onClick={() => handleTabClick('bugs')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'bugs' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <Bug className="w-5 h-5" />
              Bug Reports
            </button>
            <button onClick={() => handleTabClick('settings')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'settings' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <Settings className="w-5 h-5" />
              Settings
            </button>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition mt-4">
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </nav>
        </div>

        <div className="lg:hidden bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-2">
            <ChefHat className="w-6 h-6" />
            <h2 className="font-bold">Super Admin</h2>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setMobileMenuOpen(false)}>
            <div className="bg-white h-full w-80 max-w-full shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-red-600 text-white p-6 flex justify-between items-center">
                <h2 className="text-xl font-bold">Menu</h2>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="p-4 space-y-2">
                <button onClick={() => handleTabClick('dashboard')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'dashboard' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-medium">Dashboard</span>
                </button>
                <button onClick={() => handleTabClick('restaurants')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'restaurants' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <Users className="w-5 h-5" />
                  <span className="font-medium">Restaurants</span>
                </button>
                <button onClick={() => handleTabClick('packages')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'packages' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <Package className="w-5 h-5" />
                  <span className="font-medium">Packages</span>
                </button>
                <button onClick={() => handleTabClick('bugs')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'bugs' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <Bug className="w-5 h-5" />
                  <span className="font-medium">Bug Reports</span>
                </button>
                <button onClick={() => handleTabClick('settings')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'settings' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Settings</span>
                </button>
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-4 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition mt-4">
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </nav>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {activeTab === 'dashboard' && <SuperAdminDashboard />}
          {activeTab === 'restaurants' && <SuperAdminRestaurants />}
          {activeTab === 'packages' && <SuperAdminPackages />}
          {activeTab === 'bugs' && <SuperAdminBugReports />}
          {activeTab === 'settings' && <SuperAdminSettings />}
        </div>
      </div>
    );
  }
// Kitchen Staff View - ORDERS ONLY
if (currentView === 'kitchen') {
  // Load restaurant for kitchen staff
  // useEffect(() => {
  //   const loadKitchenRestaurant = async () => {
  //     // Check currentView here instead of wrapping the hook in an if
  //     if (restaurantId && currentView === 'kitchen') {
  //       setLoading(true);
  //       try {
  //         const rest = await StorageService.getRestaurant(restaurantId);
  //         setRestaurant(rest);
  //       } catch (error) {
  //         console.error('Error loading restaurant for kitchen:', error);
  //       } finally {
  //         setLoading(false);
  //       }
  //     }
  //   };
  //   loadKitchenRestaurant();
  // }, [restaurantId, currentView]);

  if (loading || !restaurant) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading kitchen panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChefHat className="w-6 h-6" />
          <div>
            <h2 className="font-bold">{restaurant.name}</h2>
            <p className="text-xs text-orange-100">Kitchen Staff</p>
          </div>
        </div>
        <button
          onClick={async () => {
            const staffId = localStorage.getItem('kitchenStaffId');
            const restId = localStorage.getItem('kitchenRestaurantId');
            if (staffId && restId) {
              await KitchenStaffService.logoutKitchenStaff(restId, staffId);
            }
            localStorage.removeItem('kitchenStaffId');
            localStorage.removeItem('kitchenDeviceId');
            localStorage.removeItem('kitchenRestaurantId');
            handleLogout();
          }}
          className="flex items-center gap-2 bg-white bg-opacity-20 px-3 py-1.5 rounded-lg hover:bg-opacity-30"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm">Logout</span>
        </button>
      </div>

      {/* Orders Only */}
      <div className="flex-1 overflow-y-auto">
        <OwnerOrders restaurantId={restaurantId} />
      </div>
    </div>
  );
}
  // Restaurant Owner Layout
  if (currentView === 'owner') {
    const restaurantName = restaurant ? restaurant.name : 'Restaurant';
    
    if (loading || !restaurant) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading restaurant data...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col lg:flex-row h-screen bg-gray-100">
        <BugReportButton restaurantId={restaurantId} restaurantName={restaurantName} />

        {/* ✅ DESKTOP SIDEBAR WITH AUDIT TAB */}
        <div className="hidden lg:block lg:w-64 bg-white shadow-lg overflow-y-auto">
          <div className="p-6 bg-gradient-to-r from-orange-500 to-red-600">
            <div className="flex items-center gap-3 text-white">
              <ChefHat className="w-8 h-8" />
              <div>
                <h2 className="font-bold text-lg">{restaurantName}</h2>
                <p className="text-sm text-orange-100">Owner Panel</p>
              </div>
            </div>
          </div>
          <nav className="p-4">
            <button onClick={() => handleTabClick('dashboard')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'dashboard' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <TrendingUp className="w-5 h-5" />
              Dashboard
            </button>
            <button onClick={() => handleTabClick('menu')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'menu' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <UtensilsCrossed className="w-5 h-5" />
              Menu
            </button>
            <button onClick={() => handleTabClick('categories')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'categories' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <Tag className="w-5 h-5" />
              Categories
            </button>
            <button onClick={() => handleTabClick('tables')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'tables' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <QrCode className="w-5 h-5" />
              QR Tables
            </button>
            <button onClick={() => handleTabClick('orders')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'orders' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <ShoppingCart className="w-5 h-5" />
              Orders
            </button>
            <button onClick={() => handleTabClick('invoices')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'invoices' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <FileText className="w-5 h-5" />
              Invoices
            </button>
            {/* ✅ NEW AUDIT TAB */}
            <button onClick={() => handleTabClick('audit')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'audit' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <Shield className="w-5 h-5" />
              Audit & Fraud
            </button>

            <button onClick={() => handleTabClick('inventory')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'inventory' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
  <Package className="w-5 h-5" />
  Inventory
</button>
<button onClick={() => handleTabClick('profitloss')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'profitloss' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
  <DollarSign className="w-5 h-5" />
  Profit & Loss
</button>
            <button onClick={() => handleTabClick('settings')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition ${activeTab === 'settings' ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
              <Settings className="w-5 h-5" />
              Settings
            </button>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition mt-4">
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </nav>
        </div>

        {/* MOBILE HEADER */}
        <div className="lg:hidden bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ChefHat className="w-6 h-6 flex-shrink-0" />
            <h2 className="font-bold truncate">{restaurantName}</h2>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell 
              restaurantId={restaurantId}
              onNavigateToOrders={() => handleTabClick('orders')}
            />
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg flex-shrink-0">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* DESKTOP CONTENT AREA */}
        <div className="hidden lg:flex flex-col flex-1 overflow-hidden">
          <div className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {activeTab === 'dashboard' && 'Dashboard'}
                {activeTab === 'menu' && 'Menu Management'}
                {activeTab === 'categories' && 'Categories'}
                {activeTab === 'tables' && 'QR Tables'}
                {activeTab === 'orders' && 'Orders'}
                {activeTab === 'invoices' && 'Invoice Management'}
                {activeTab === 'audit' && 'Audit & Fraud Prevention'}
                {activeTab === 'settings' && 'Settings'}
                {activeTab === 'profitloss' && 'Profit & Loss Statement'}
                {activeTab === 'inventory' && 'Inventory Management'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell 
                restaurantId={restaurantId}
                onNavigateToOrders={() => handleTabClick('orders')}
              />
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition px-3 py-2 rounded-lg hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {activeTab === 'dashboard' && <OwnerDashboard restaurantId={restaurantId} />}
            {activeTab === 'menu' && (
              <OwnerMenu
                restaurantId={restaurantId}
                onNavigateToCategories={() => handleTabClick('categories')}
              />
            )}
            {activeTab === 'categories' && <Categories restaurantId={restaurantId} />}
            {activeTab === 'tables' && <Tables restaurantId={restaurantId} />}
            {activeTab === 'orders' && <OwnerOrders restaurantId={restaurantId} />}
            {activeTab === 'invoices' && <InvoiceManagementPanel restaurantId={restaurantId} />}
            {/* ✅ NEW AUDIT DASHBOARD */}
            {activeTab === 'audit' && <AuditDashboard restaurantId={restaurantId} />}
            {activeTab === 'inventory' && <Inventory restaurantId={restaurantId} />}
            {activeTab === 'profitloss' && <ProfitLoss restaurantId={restaurantId} />}


            {activeTab === 'settings' && <OwnerSettings restaurantId={restaurantId} />}
          </div>
        </div>

        {/* MOBILE CONTENT AREA */}
        <div className="lg:hidden flex-1 overflow-y-auto pb-20">
          {activeTab === 'dashboard' && <OwnerDashboard restaurantId={restaurantId} />}
          {activeTab === 'menu' && (
            <OwnerMenu
              restaurantId={restaurantId}
              onNavigateToCategories={() => handleTabClick('categories')}
            />
          )}
          {activeTab === 'categories' && <Categories restaurantId={restaurantId} />}
          {activeTab === 'tables' && <Tables restaurantId={restaurantId} />}
          {activeTab === 'orders' && <OwnerOrders restaurantId={restaurantId} />}
          {activeTab === 'invoices' && <InvoiceManagementPanel restaurantId={restaurantId} />}
          {/* ✅ NEW AUDIT DASHBOARD FOR MOBILE */}
          {activeTab === 'audit' && <AuditDashboard restaurantId={restaurantId} />}
          {activeTab === 'inventory' && <Inventory restaurantId={restaurantId} />}
{activeTab === 'profitloss' && <ProfitLoss restaurantId={restaurantId} />}
          {activeTab === 'settings' && <OwnerSettings restaurantId={restaurantId} />}
        </div>

        {/* ✅ MOBILE MENU WITH AUDIT TAB */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setMobileMenuOpen(false)}>
            <div className="bg-white h-full w-80 max-w-full shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-red-600 text-white p-6 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">{restaurantName}</h2>
                  <p className="text-sm text-orange-100">Owner Panel</p>
                </div>
                <button onClick={() => setMobileMenuOpen(false)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="p-4 space-y-2">
                <button onClick={() => handleTabClick('dashboard')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'dashboard' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-medium">Dashboard</span>
                </button>
                <button onClick={() => handleTabClick('menu')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'menu' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <UtensilsCrossed className="w-5 h-5" />
                  <span className="font-medium">Menu</span>
                </button>
                <button onClick={() => handleTabClick('categories')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'categories' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <Tag className="w-5 h-5" />
                  <span className="font-medium">Categories</span>
                </button>
                <button onClick={() => handleTabClick('tables')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'tables' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <QrCode className="w-5 h-5" />
                  <span className="font-medium">QR Tables</span>
                </button>
                <button onClick={() => handleTabClick('orders')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'orders' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <ShoppingCart className="w-5 h-5" />
                  <span className="font-medium">Orders</span>
                </button>
                <button onClick={() => handleTabClick('invoices')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'invoices' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <FileText className="w-5 h-5" />
                  <span className="font-medium">Invoices</span>
                </button>
                {/* ✅ NEW AUDIT TAB IN MOBILE MENU */}
                <button onClick={() => handleTabClick('audit')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'audit' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <Shield className="w-5 h-5" />
                  <span className="font-medium">Audit & Fraud</span>
                </button>
                <button onClick={() => handleTabClick('inventory')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'inventory' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
  <Package className="w-5 h-5" />
  <span className="font-medium">Inventory</span>
</button>
<button onClick={() => handleTabClick('profitloss')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'profitloss' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
  <DollarSign className="w-5 h-5" />
  <span className="font-medium">Profit & Loss</span>
</button>
                <button onClick={() => handleTabClick('settings')} className={`w-full flex items-center gap-3 px-4 py-4 rounded-lg transition ${activeTab === 'settings' ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}>
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Settings</span>
                </button>
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-4 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition mt-4">
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;